import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { signInCoach, getPlatformConfig, sendPasswordReset, renderAvisoMyklubb } from '../lib/supabase';
import LegalDocLink from './legal/LegalDocLink';

const PLATFORM_CACHE_KEY = 'myklubb_platform_config';

const Login = () => {
  const { clearError, error } = useAuth();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  // Inicializar desde caché para pintar los colores de marca en el primer render
  // (evita el flash de colores por defecto -> marca al cargar getPlatformConfig).
  const [platform, setPlatform] = useState(() => {
    try {
      const cached = localStorage.getItem(PLATFORM_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    getPlatformConfig().then(({ data }) => {
      if (data) {
        setPlatform(data);
        try {
          localStorage.setItem(PLATFORM_CACHE_KEY, JSON.stringify(data));
        } catch {
          // ignore storage errors (modo privado, etc.)
        }
      }
    });
  }, []);

  const friendlyAuthError = (message) => {
    const m = (message || '').toLowerCase();
    if (m.includes('invalid login credentials')) return 'Correo o contraseña incorrectos.';
    if (m.includes('email not confirmed')) return 'Tu cuenta aún no está confirmada. Revisa tu correo o usa "¿Olvidaste tu contraseña?".';
    if (m.includes('rate limit') || m.includes('too many')) return 'Demasiados intentos. Espera un momento e inténtalo de nuevo.';
    return 'No pudimos iniciar sesión. Revisa tus datos e inténtalo de nuevo.';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError(null);
    clearError();

    try {
        const { error } = await signInCoach(loginData.email, loginData.password);

        if (error) {
            setLoginError(friendlyAuthError(error.message));
            setIsLoading(false);
            return;
        }

        // Sin navigate: la ruta '/login' de App.js redirige a landingPath en cuanto
        // hay sesion (platform admin -> /platform, admin/head_coach -> /groups,
        // resto -> /hoy). Navegar aqui mandaba a TODOS a /groups.

    } catch (err) {
        setLoginError('Error de conexión. Inténtalo de nuevo.');
    } finally {
        setIsLoading(false);
    }
    };

  const handleForgot = async () => {
    if (!loginData.email) {
      setResetMsg({ type: 'info', text: 'Escribe tu email arriba y vuelve a tocar el enlace.' });
      return;
    }
    setResetLoading(true);
    setResetMsg(null);
    const { error } = await sendPasswordReset(loginData.email);
    setResetLoading(false);
    if (error) {
      setResetMsg({ type: 'error', text: 'No pudimos enviar el correo. Revisa el email e intenta de nuevo.' });
    } else {
      setResetMsg({ type: 'ok', text: `Te enviamos un correo a ${loginData.email} para restablecer tu contraseña.` });
    }
  };

  const pName = platform?.platform_name || 'MYKLUBB';
  const pSubtitle = platform?.login_subtitle || 'Control de tu club deportivo';
  const pLogo = platform?.login_logo_url || '/assets/myklubb-logo-v-light.svg';
  // Defaults = marca MYKLUBB (bg dark, texto blanco, acento volt) para minimizar
  // el flash en el primer ingreso sin caché.
  const pBg = platform?.login_color_bg || '#0D0D0D';
  const pText = platform?.login_color_text || '#F2F2F3';
  const pAccent = platform?.login_color_accent || '#B3F000';

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden" style={{ backgroundColor: pBg }}>
      {/* Foto de fondo: estadio con reflectores volt (casi a plena opacidad para que brillen) */}
      <div className="pointer-events-none absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/assets/login-bg.jpg)', opacity: 0.95 }} />
      {/* Pozo suave detras del contenido (legibilidad) sin apagar los reflectores de las esquinas */}
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(circle at 50% 44%, ${pBg}99 0%, ${pBg}40 26%, transparent 46%)` }} />
      {/* Glow volt sutil */}
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 50% 3%, ${pAccent}1A, transparent 50%)` }} />

      <div className="relative w-full max-w-[440px] rounded-3xl p-10"
        style={{
          backgroundColor: pBg,
          backgroundImage: `linear-gradient(${pText}0A, ${pText}03)`,
          border: `1px solid ${pText}14`,
          boxShadow: '0 24px 70px -24px rgba(0,0,0,0.85)',
        }}>

        {/* Acento volt superior */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-24 rounded-full" style={{ backgroundColor: pAccent, boxShadow: `0 0 18px ${pAccent}` }} />

        {/* Logo + headline */}
        <div className="text-center mb-8">
          <img
            src={pLogo}
            alt={pName}
            className="h-20 w-auto max-w-[72%] mx-auto object-contain"
            onError={(e) => { e.target.src = '/assets/myklubb-icon-light.svg' }}
          />
          <h1 className="mt-6 text-[26px] leading-tight font-extrabold tracking-tight" style={{ color: pText }}>
            Inicia sesión
          </h1>
          {pSubtitle && (
            <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: `${pText}59` }}>
              {pSubtitle}
            </p>
          )}
        </div>

        {(loginError || error) && (
          <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: '#FF4D4D14', border: '1px solid #FF4D4D40' }}>
            <p className="text-sm font-medium" style={{ color: '#FF8A8A' }}>{loginError || error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-[11px] font-semibold mb-2 uppercase tracking-[0.14em]" style={{ color: `${pText}80` }}>
              Email
            </label>
            <input
              type="email"
              value={loginData.email}
              onChange={(e) => setLoginData({...loginData, email: e.target.value})}
              onFocus={(e) => { e.target.style.borderColor = pAccent; e.target.style.boxShadow = `0 0 0 3px ${pAccent}22`; }}
              onBlur={(e) => { e.target.style.borderColor = `${pText}1F`; e.target.style.boxShadow = 'none'; }}
              className="w-full px-4 py-3.5 rounded-xl focus:outline-none transition-all duration-200 text-[15px]"
              style={{ backgroundColor: `${pText}0A`, border: `1px solid ${pText}1F`, color: pText }}
              placeholder="coach@example.com"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold mb-2 uppercase tracking-[0.14em]" style={{ color: `${pText}80` }}>
              Contraseña
            </label>
            <input
              type="password"
              value={loginData.password}
              onChange={(e) => setLoginData({...loginData, password: e.target.value})}
              onFocus={(e) => { e.target.style.borderColor = pAccent; e.target.style.boxShadow = `0 0 0 3px ${pAccent}22`; }}
              onBlur={(e) => { e.target.style.borderColor = `${pText}1F`; e.target.style.boxShadow = 'none'; }}
              className="w-full px-4 py-3.5 rounded-xl focus:outline-none transition-all duration-200 text-[15px]"
              style={{ backgroundColor: `${pText}0A`, border: `1px solid ${pText}1F`, color: pText }}
              placeholder="••••••••••••"
              required
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full font-bold py-3.5 px-6 rounded-xl transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 flex items-center justify-center gap-3 transform hover:-translate-y-0.5 text-[15px] mt-2"
            style={{ backgroundColor: pAccent, color: pBg, boxShadow: `0 10px 30px -8px ${pAccent}66` }}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent" style={{ borderColor: pBg }}></div>
                <span>Ingresando...</span>
              </>
            ) : (
              <span>Ingresar</span>
            )}
          </button>
        </form>

        <button
          type="button"
          onClick={handleForgot}
          disabled={resetLoading}
          className="mt-5 mx-auto block text-[13px] font-medium transition-colors disabled:opacity-60"
          style={{ color: `${pText}80` }}
          onMouseEnter={(e) => { e.currentTarget.style.color = pAccent }}
          onMouseLeave={(e) => { e.currentTarget.style.color = `${pText}80` }}
        >
          {resetLoading ? 'Enviando…' : '¿Olvidaste tu contraseña?'}
        </button>

        {resetMsg && (
          <p className="mt-3 text-center text-[13px] leading-snug" style={{ color: resetMsg.type === 'error' ? '#FF8A8A' : resetMsg.type === 'ok' ? pAccent : `${pText}99` }}>
            {resetMsg.text}
          </p>
        )}

        <div className="mt-6 pt-4 text-center flex items-center justify-center gap-3" style={{ borderTop: `1px solid ${pText}0A` }}>
          <a
            href="https://myklubb.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12px] transition-colors"
            style={{ color: `${pText}40` }}
            onMouseEnter={(e) => { e.currentTarget.style.color = pAccent }}
            onMouseLeave={(e) => { e.currentTarget.style.color = `${pText}40` }}
          >
            Conoce MYKLUBB →
          </a>
          <span className="text-[12px]" style={{ color: `${pText}20` }}>·</span>
          <LegalDocLink doc={renderAvisoMyklubb(platform?.legal_entity || {})}>
            <button
              type="button"
              className="text-[12px] transition-colors"
              style={{ color: `${pText}40` }}
              onMouseEnter={(e) => { e.currentTarget.style.color = pAccent }}
              onMouseLeave={(e) => { e.currentTarget.style.color = `${pText}40` }}
            >
              Aviso de Privacidad
            </button>
          </LegalDocLink>
        </div>
      </div>
    </div>
  );
};

export default Login;
